/*
  Rebuy Gift Handler
  
  Manages code gifts and automatic gifts with purchase:
  - Works with both manually applied and URL-based discount codes
  - Reconciles code/MOV gifts and per-unit automatic gift entitlements
  - Hides quantity selectors to prevent incrementing free gifts
  - Can be toggled on/off via Admin settings
*/

window.FreeGiftHandler = (function () {
  // Private variables
  let settings = null;
  let validGifts = {};
  let autoGiftRules = [];
  let initialised = false;
  let reconciliationRunning = false;
  let reconciliationPending = false;
  let reconciliationPromise = null;
  let failedState = null;
  let suspendedState = null;
  const MAX_GIFT_MUTATIONS = 30;
  let debug = false;

  function log(...args) {
  if (debug) {
    console.log('[FreeGiftHandler]', ...args);
  }
}


  // Constants
  const COOKIE_NAME = 'active_discount_code';
  const URL_COOKIE_NAME = 'url_discount_code';
  const GIFT_QTY_HIDE_STYLE_ID = 'bwb-free-gift-hide-qty-controls';
  const CODE_GIFT_SOURCE = 'bwb_code';
  const AUTO_GIFT_SOURCE = 'bwb_auto';

  // Cookie utilities
  function setCookie(name, value) {
    const domain = window.location.hostname;
    const secure = window.location.protocol === 'https:';
    let cookie = `${name}=${value}; path=/; domain=${domain}; max-age=86400`;
    if (secure) cookie += '; Secure';
    document.cookie = cookie;
    log(`[DE Cookie] Set: ${cookie}`);
  }

  function clearCookie(name) {
    const domain = window.location.hostname;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    // log(`[DE Cookie] Cleared: ${name}`);
  }

  function getCookie(name) {
    // log(`[Cookie Debug] Getting cookie ${name}`);
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
    // log(`[Cookie Debug] Value for ${name}: ${cookieValue}`);
    return cookieValue;
  }

  function ensureGiftQuantityControlsHidden() {
    if (document.getElementById(GIFT_QTY_HIDE_STYLE_ID)) return;

    const styleEl = document.createElement('style');
    styleEl.id = GIFT_QTY_HIDE_STYLE_ID;
    styleEl.textContent = `
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_code"] .rebuy-cart__flyout-item-quantity-widget {
        display: none !important;
      }
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget-button,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget button,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget input,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget select,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-remove {
        display: none !important;
      }
    `;
    document.head.appendChild(styleEl);
  }

  // Helper to process gift configuration
  function setupGiftConfiguration() {
    if (!settings || !settings.freeGifts) return {};

    // Transform the freeGifts array into a map for easier lookup
    return settings.freeGifts.reduce((acc, gift) => {
      if (gift.discountCodes && gift.product) {
        gift.discountCodes.forEach(code => {
          const normalizedCode = code.trim().toUpperCase();
          if (normalizedCode) {
            acc[normalizedCode] = {
              product: String(gift.product),
              minSubtotal: Number(gift.minSubtotal),
            };
          }
        });
      }
      return acc;
    }, {});
  }

  function setupAutoGiftConfiguration() {
    const offers = window.autoGiftSettings?.offers;
    if (!Array.isArray(offers)) return [];

    const rules = offers.slice(0, 2).flatMap(offer => {
      if (!offer?.enabled) return [];

      const triggerProduct = String(offer.triggerProduct || '').trim();
      const triggerVariant = String(offer.triggerVariant || '').trim();
      const giftVariant = String(offer.giftVariant || '').trim();
      if (
        !/^\d+$/.test(triggerProduct) || !/^\d+$/.test(triggerVariant) || !/^\d+$/.test(giftVariant) ||
        Number(offer.triggerVariantCount) !== 1 || offer.giftPrice == null ||
        Number(offer.giftPrice) !== 0 || triggerVariant === giftVariant
      ) {
        console.warn('[FreeGiftHandler] Invalid auto gift configuration in slot', offer.slot);
        return [];
      }

      return [{ triggerProduct, triggerVariant, giftVariant }];
    });

    // A selected gift must never become a trigger for another active offer.
    return rules.filter(rule => {
      if (rules.some(other => other.giftVariant === rule.triggerVariant || other.triggerVariant === rule.giftVariant)) {
        console.warn('[FreeGiftHandler] Auto gift offers cannot award qualifying products');
        return false;
      }
      return true;
    });
  }

  function getVariantId(item) {
    return String(item.variant_id ?? item.id);
  }

  function collectAutoGiftMutations(cart) {
    const desiredQuantities = new Map();
    autoGiftRules.forEach(rule => {
      const quantity = cart.items.reduce((total, item) => {
        const isCustomGift = [CODE_GIFT_SOURCE, 'bwb_subscription', AUTO_GIFT_SOURCE]
          .includes(item.properties?._free_gift_source);
        const matchesProduct = item.product_id == null || String(item.product_id) === rule.triggerProduct;
        return !isCustomGift && matchesProduct && getVariantId(item) === rule.triggerVariant
          ? total + Number(item.quantity || 0)
          : total;
      }, 0);
      desiredQuantities.set(rule.giftVariant, (desiredQuantities.get(rule.giftVariant) || 0) + quantity);
    });

    const mutations = [];
    const retainedVariants = new Set();
    cart.items.forEach(item => {
      if (item.properties?._free_gift_source !== AUTO_GIFT_SOURCE) return;
      const variantId = getVariantId(item);
      const quantity = desiredQuantities.get(variantId) || 0;
      if (!quantity || retainedVariants.has(variantId)) {
        mutations.push({ type: 'remove', itemKey: item.key });
        return;
      }
      retainedVariants.add(variantId);
      if (item.quantity !== quantity) {
        mutations.push({ type: 'setQuantity', itemKey: item.key, quantity });
      }
    });
    desiredQuantities.forEach((quantity, variantId) => {
      if (quantity > 0 && !retainedVariants.has(variantId)) {
        mutations.push({
          type: 'add', variantId, quantity,
          properties: { _free_gift_source: AUTO_GIFT_SOURCE },
        });
      }
    });
    return mutations;
  }

  // Detect URL-based discount codes by looking for discount messages on cart items
  function detectAutomaticDiscount() {
    // Method 1: Look for line item messages (works for percentage discounts)
    const discountElements = document.querySelectorAll('.rebuy-cart__flyout-item-discount-message');
    let detectedCode = null;

    discountElements.forEach(el => {
      const discountText = el.textContent.trim().toUpperCase();
      if (validGifts[discountText]) {
        detectedCode = discountText;
      }
    });

    // Method 2: Fallback to Shopify's discount_code cookie (for 0% discounts)
    if (!detectedCode) {
      const shopifyCode = getCookie('discount_code');
      if (shopifyCode && validGifts[shopifyCode.toUpperCase()]) {
        detectedCode = shopifyCode.toUpperCase();
        log('[GIFT DEBUG] Found code from Shopify cookie:', detectedCode);
      }
    }

    return detectedCode;
  }

  // Handle URL-based discounts
  function handleURLDiscount() {
    const detectedDiscount = detectAutomaticDiscount();

    if (detectedDiscount) {
      log(`[FreeGiftHandler] Detected automatic discount: ${detectedDiscount}`);
      setCookie(URL_COOKIE_NAME, detectedDiscount);
      return detectedDiscount;
    } else {
      clearCookie(URL_COOKIE_NAME);
      return null;
    }
  }

  function collectCodeGiftMutations(cart) {
    if (!settings.enableFreeGift) return [];
    const manualDiscount = getCookie(COOKIE_NAME)?.toUpperCase().trim();
    const urlDiscount = detectAutomaticDiscount();
    let activeCode = manualDiscount;
    if (!activeCode && urlDiscount) {
      activeCode = urlDiscount;
      setCookie(URL_COOKIE_NAME, urlDiscount);
    } else if (!activeCode && !urlDiscount) {
      clearCookie(URL_COOKIE_NAME);
    }

    const activeGift = activeCode ? validGifts[activeCode] : null;
    const subtotal = cart.items_subtotal_price || 0;
    const allGiftVariants = Object.values(validGifts).map(gift => gift.product);
    const giftVariant = activeGift && subtotal >= activeGift.minSubtotal ? activeGift.product : null;
    const mutations = [];
    const matchingGifts = cart.items.filter(item =>
      item.properties?._free_gift_source === CODE_GIFT_SOURCE && getVariantId(item) === giftVariant
    );
    if (giftVariant && !matchingGifts.length) {
      mutations.push({ type: 'add', variantId: giftVariant });
    }
    const duplicateKeys = new Set(matchingGifts.slice(1).map(item => item.key));
    cart.items.forEach(item => {
      if (item.properties?._free_gift_source !== CODE_GIFT_SOURCE ||
          parseInt(item.price) !== 0 || !allGiftVariants.includes(getVariantId(item))) return;
      if (!giftVariant || getVariantId(item) !== giftVariant || duplicateKeys.has(item.key)) {
        mutations.push({ type: 'remove', itemKey: item.key });
      } else if (item.quantity > 1) {
        mutations.push({ type: 'setQuantity', itemKey: item.key, quantity: 1 });
      }
    });
    if (!manualDiscount || !validGifts[manualDiscount]) clearCookie(COOKIE_NAME);
    return mutations;
  }

  function collectGiftMutations(cart) {
    return [...collectAutoGiftMutations(cart), ...collectCodeGiftMutations(cart)];
  }

  function getReconciliationState(cart) {
    return JSON.stringify({
      items: cart.items.map(item => [item.key, getVariantId(item), item.product_id,
        item.quantity, item.price, item.properties]),
      subtotal: cart.items_subtotal_price,
      manualCode: getCookie(COOKIE_NAME),
      urlCode: detectAutomaticDiscount(),
    });
  }

  async function applyMutation(mutation) {
    if (mutation.type === 'add') {
      return window.RebuyUtils.addGiftToCart(mutation.variantId, mutation.properties, mutation.quantity);
    }
    // Never fall back to a variant ID: another promotion may own the same variant.
    if (!mutation.itemKey) throw new Error('Gift mutation requires a cart line key');
    if (mutation.type === 'remove') {
      return window.RebuyUtils.removeItemFromCart(mutation.itemKey);
    }
    return window.RebuyUtils.setItemQuantity(mutation.itemKey, mutation.quantity);
  }

  async function reconcileGifts() {
    let attemptedMutation = false;
    let mutationCount = 0;
    try {
      while (true) {
        reconciliationPending = false;
        // Rebuy refresh can emit cart events itself. Read Shopify directly here.
        const cart = await window.RebuyUtils.fetchCart({ useRebuy: false });
        if (!Array.isArray(cart?.items)) break;
        const mutations = collectGiftMutations(cart);
        const state = getReconciliationState(cart);
        if (state === suspendedState || state === failedState) break;
        if (!mutations.length) {
          if (reconciliationPending) continue;
          break;
        }
        if (mutationCount >= MAX_GIFT_MUTATIONS) {
          suspendedState = state;
          console.warn('[FreeGiftHandler] Gift reconciliation paused after repeated cart changes');
          break;
        }

        attemptedMutation = true;
        const result = await applyMutation(mutations[0]);
        mutationCount += 1;
        if (!result) {
          // An add may have reached Shopify despite a lost response. The next
          // attempt must re-read the cart; matching failed states stay suppressed.
          failedState = state;
          break;
        }
        // Recalculate from a fresh cart after every write, including new line keys.
      }
    } finally {
      if (attemptedMutation && typeof window.Rebuy?.Cart?.getCart === 'function') {
        try {
          await window.Rebuy.Cart.getCart();
        } catch (error) {
          console.error('[FreeGiftHandler] Could not refresh Rebuy after gift changes:', error);
        }
      }
    }
  }

  function manageGift(retryFailed = false) {
    reconciliationPending = true;
    if (reconciliationRunning) return reconciliationPromise;
    if (retryFailed) {
      failedState = null;
      suspendedState = null;
    }
    reconciliationRunning = true;
    reconciliationPromise = reconcileGifts()
      .catch(error => {
        reconciliationPending = false;
        console.error('[FreeGiftHandler] Gift reconciliation failed:', error);
      })
      .finally(() => {
        reconciliationRunning = false;
        if (reconciliationPending) manageGift();
      });
    return reconciliationPromise;
  }

  // Handle manual discount code application
  function setupDiscountListeners() {
    document.body.addEventListener('click', async e => {
      const applyBtn = e.target.closest('.rebuy-cart__discount-form button');
      if (!applyBtn) return;

      const input = document.querySelector('#rebuy-cart__discount-input');
      const code = input?.value?.trim()?.toUpperCase();
      if (!code || !validGifts[code]) return;

      log(`Applying gift code: ${code}`);
      setCookie(COOKIE_NAME, code);

      // Verify cookie was set
      await new Promise(resolve => setTimeout(resolve, 300));
      if (getCookie(COOKIE_NAME) !== code) {
        console.warn('Cookie not persisted! Using localStorage fallback');
        localStorage.setItem(`gift_code_fallback`, code);
      }
    });
  }

  function setupRemovalListeners() {
    document.body.addEventListener('click', async e => {
      if (!e.target.closest('.rebuy-cart__discount-tag-remove')) return;

      const currentCode = getCookie(COOKIE_NAME);
      if (!currentCode) return;

      // ONLY clear if it's one of our gift codes
      if (validGifts[currentCode.toUpperCase()]) {
        log(`Clearing gift code cookie: ${currentCode}`);
        clearCookie(COOKIE_NAME);
      }
    });
  }

  // Public API
  return {
    init: function () {
      if (initialised || !window.RebuyEventManager) return this;
      initialised = true;
      settings = window.discountGiftSettings || {};
      validGifts = settings.enableFreeGift ? setupGiftConfiguration() : {};
      autoGiftRules = setupAutoGiftConfiguration();
      ensureGiftQuantityControlsHidden();
      // Delegated listeners cover dynamically rendered discount controls.
      setupRemovalListeners();
      setupDiscountListeners();
      window.RebuyEventManager.on('cart:change', () => manageGift())
        .on('cart:ready', () => manageGift())
        .on('cart:show', () => manageGift(true))
        .on('discount:applied', () => manageGift())
        .on('discount:removed', () => manageGift());
      manageGift();
      return this;
    },

    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },

    // Helper method to check if a code is valid for a gift
    isValidGiftCode: function (code) {
      if (!code) return false;
      const normalizedCode = code.trim().toUpperCase();
      return !!validGifts[normalizedCode];
    },

    // Helper to get current active gift code
    getActiveCode: function () {
      // Try cookie first
      const cookieValue = getCookie(COOKIE_NAME) || getCookie(URL_COOKIE_NAME);
      if (cookieValue) return cookieValue;

      // If cookie fails, check localStorage
      try {
        return localStorage.getItem(`gift_code_${COOKIE_NAME}`);
      } catch (e) {
        return null;
      }
    },
  };
})();
